import { useState, useEffect } from "react";
import ModalNew from "../popup/Modal";
import { PopupHeader, PopupContent } from "../popup/common";
import SmallAlert from "../alerts/SmallAlert";

function DocumentUploadModal({
  isOpen,
  onClose,
  onUploadSuccess,
  onUploadError,
  uploadFunction,
  selectedSource,
  selectedDocumentId,
  title = "Upload Document",
  allowedFileTypes: customAllowedFileTypes = null,
  allowedExtensions: customAllowedExtensions = null,
  taxYears = null, // Array of tax years for year selector (for crypto transactions)
  selectedYear: initialSelectedYear = null, // Initial selected year
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(initialSelectedYear);
  const [uploadError, setUploadError] = useState("");

  // Default allowed file types
  const defaultAllowedFileTypes = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  ];

  const defaultAllowedExtensions = [".png", ".jpg", ".jpeg", ".pdf", ".docx"];

  // Use custom file types if provided, otherwise use defaults
  const allowedFileTypes = customAllowedFileTypes || defaultAllowedFileTypes;
  const allowedExtensions = customAllowedExtensions || defaultAllowedExtensions;

  const getFileIcon = (fileType) => {
    if (fileType.startsWith("image/")) {
      return "🖼️";
    } else if (fileType === "application/pdf") {
      return "📄";
    } else if (fileType.includes("wordprocessingml")) {
      return "📝";
    } else if (
      fileType === "text/csv" ||
      fileType === "application/csv" ||
      fileType === "text/comma-separated-values"
    ) {
      return "📊";
    }
    return "📎";
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (uploadError) {
      setUploadError("");
    }

    // Validate file type - check both MIME type and extension
    const fileExtension = "." + file.name.split(".").pop().toLowerCase();
    const isValidMimeType = allowedFileTypes.includes(file.type);
    const isValidExtension = allowedExtensions.some(
      (ext) =>
        fileExtension === ext.toLowerCase() ||
        file.name.toLowerCase().endsWith(ext.toLowerCase()),
    );

    // For CSV files, also check common CSV MIME types
    const isCsvFile =
      fileExtension === ".csv" ||
      file.type === "text/csv" ||
      file.type === "application/csv" ||
      file.type === "text/comma-separated-values" ||
      file.type === "application/vnd.ms-excel";

    // If CSV is allowed, accept CSV files
    const csvAllowed = allowedExtensions.includes(".csv");
    const isValidFile =
      (csvAllowed && isCsvFile) || isValidMimeType || isValidExtension;

    if (!isValidFile) {
      const formatText = allowedExtensions
        .map((ext) => ext.toUpperCase().replace(".", ""))
        .join(", ");
      alert(`Please select a valid file type: ${formatText}`);
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB");
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    console.log("MODAL PROPS:", { selectedSource, selectedDocumentId });

    if (!selectedFile || !uploadFunction) return;

    // For crypto transactions, validate year selection
    if (taxYears && taxYears.length > 0 && !selectedYear) {
      alert("Please select a tax year");
      return;
    }

    setIsUploading(true);
    setUploadError("");

    try {
      // Pass selectedYear to uploadFunction if it's a crypto transaction
      const result = await uploadFunction(
        selectedFile,
        selectedSource,
        selectedDocumentId,
        selectedYear,
      );

      // Clear state
      setSelectedFile(null);
      setSelectedYear(initialSelectedYear);

      // Call success callback
      if (onUploadSuccess) {
        onUploadSuccess(result);
      }

      // Close modal
      handleClose();
    } catch (error) {
      if (onUploadError) {
        onUploadError(
          error.message || "Failed to upload document. Please try again.",
        );
      }
      setUploadError(
        error.message || "Failed to upload document. Please try again.",
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setSelectedYear(initialSelectedYear);
    setUploadError("");
    if (onClose) {
      onClose();
    }
  };

  // Update selectedYear when initialSelectedYear changes
  useEffect(() => {
    setSelectedYear(initialSelectedYear);
  }, [initialSelectedYear]);

  if (!isOpen) return null;

  return (
    <ModalNew className="content-modal">
      <PopupHeader
        title={title}
        setOpenState={(isOpen) => {
          if (!isOpen) {
            handleClose();
          }
        }}
        icon={`${process.env.PUBLIC_URL}/images/icons/upload_blue_popup.svg`}
        variant="compact"
      />

      <PopupContent>
        {uploadError && (
          <div className="alert-container">
            <SmallAlert
              alert={true}
              type="error"
              message={uploadError}
              autoDismissOnInteraction={true}
              onDismiss={() => setUploadError("")}
            />
          </div>
        )}
        <form onSubmit={handleUpload} className="avatar-upload-form" noValidate>
          <div className="modal-content">
            <div className="file-input-section">
              <input
                type="file"
                name="document"
                id="document-upload"
                accept={allowedExtensions.join(",")}
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <div
                className="drag-drop-area"
                onClick={(e) => {
                  e.preventDefault();
                  const fileInput = document.getElementById("document-upload");
                  if (fileInput) {
                    fileInput.click();
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add("drag-over");
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove("drag-over");
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("drag-over");
                  const files = e.dataTransfer.files;
                  if (files.length > 0) {
                    const file = files[0];
                    const event = { target: { files: [file] } };
                    handleFileSelect(event);
                  }
                }}
              >
                <div className="drag-drop-content">
                  {selectedFile ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div style={{ fontSize: "48px" }}>
                        {getFileIcon(selectedFile.type)}
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <p style={{ fontWeight: "bold", margin: "0 0 4px 0" }}>
                          {selectedFile.name}
                        </p>
                        <p
                          style={{ color: "#666", fontSize: "14px", margin: 0 }}
                        >
                          {formatFileSize(selectedFile.size)}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="cta-button secondary short text-center"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedFile(null);
                          const fileInput =
                            document.getElementById("document-upload");
                          if (fileInput) {
                            fileInput.value = "";
                          }
                        }}
                      >
                        Change File
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="drag-drop-icon">📁</div>
                      <p>Drag & drop a file here</p>
                      <p className="drag-drop-or">or</p>
                      <button
                        type="button"
                        className="cta-button secondary short text-center"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const fileInput =
                            document.getElementById("document-upload");
                          if (fileInput) {
                            fileInput.click();
                          }
                        }}
                      >
                        Browse Files
                      </button>
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#999",
                          marginTop: "8px",
                        }}
                      >
                        Accepted formats:{" "}
                        {allowedExtensions
                          .map((ext) => ext.toUpperCase().replace(".", ""))
                          .join(", ")}{" "}
                        (max 10MB)
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Year selector for crypto transactions */}
            {taxYears && taxYears.length > 0 && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "12px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "4px",
                }}
              >
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1A2127",
                  }}
                >
                  Select Tax Year *
                </label>
                <select
                  value={selectedYear || ""}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  required
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                    backgroundColor: "white",
                  }}
                >
                  <option value="">Select a year</option>
                  {taxYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="cta-button secondary short text-center"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="cta-button primary short text-center"
              disabled={
                !selectedFile ||
                isUploading ||
                (taxYears && taxYears.length > 0 && !selectedYear)
              }
            >
              {isUploading ? "Uploading..." : "Upload Document"}
            </button>
          </div>
        </form>
      </PopupContent>
    </ModalNew>
  );
}

export default DocumentUploadModal;
